import React, { useContext, useEffect, useState } from 'react';
import { IPaginationParams } from '../../../services/types';
import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import { Add } from '@mui/icons-material';
import { AppContext } from '../../../context/AppContextProvider';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  ISportHallFilterParams,
  ISportHallProps,
  ISportHallSimpleProps,
} from '../../../services/Sports/Halls/types';
import {
  useGetAllSportHallsByLicenseAsync,
  useGetSportHallsByLicenseCountAsync,
} from '../../../services/Sports/Halls/hooks/useQuery';
import {
  useDeleteSportHall,
  useGetSportHall,
} from '../../../services/Sports/Halls/hooks/useMutation';
import { SportHallEditModal } from './_components/SportHallEditModal';
import { t } from 'i18next';

const SportsHallsPage = () => {
  const [isVisibleEditModal, setIsVisibleEditModal] = useState<boolean>(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] =
    useState<boolean>(false);
  const [filter, setFilter] = useState<ISportHallFilterParams>({
    search: '',
  });
  const [paginationParams, setPaginationParams] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [selectedToEdit, setSelectedToEdit] = useState<ISportHallProps>();
  const [selectedToDelete, setSelectedToDelete] =
    useState<ISportHallSimpleProps>();
  const {
    data: halls,
    refetch: refetchHalls,
    isLoading: isLoadingHalls,
  } = useGetAllSportHallsByLicenseAsync(paginationParams, filter);
  const {
    data: totalHalls,
    refetch: refetchHallsCount,
    isLoading: isLoadingCount,
  } = useGetSportHallsByLicenseCountAsync(paginationParams, filter);
  const { mutateAsync: mutateGetHall } = useGetSportHall();
  const { mutateAsync: mutateDeleteHall } = useDeleteSportHall();

  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  const columns: IColumnProps[] = [
    {
      id: 'name',
      label: t('sportsPage.blocks.sportBlockModal.name'),
      width: 'auto',
    },
    {
      id: 'sportUnity.name',
      label: t('sportsPage.blocks.sportBlockModal.unitySports'),
      width: 'auto',
    },
    {
      id: 'id',
      label: t('sportsPage.blocks.actions'),
      align: 'center',
      width: '0px',
      render: (id) => (
        <ButtonActionsComponent
          key={id}
          isWithDefaultOptionsList
          handleDelete={() => handleDelete(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const refetchAll = async () => {
    setIsShowLoadingComponent(true);
    await refetchHalls();
    await refetchHallsCount();
  };

  const handleEdit = async (id: number) => {
    await mutateGetHall(id).then(setSelectedToEdit);
    setIsVisibleEditModal(true);
  };

  const handleDelete = async (id: number) => {
    setSelectedToDelete(halls?.find((i) => i.id === id));
    setIsVisibleDeleteModal(true);
  };

  const handleConfirmDeletion = async () => {
    if (selectedToDelete && selectedToDelete.id) {
      await mutateDeleteHall(selectedToDelete.id).then(() => {
        setSelectedToDelete(undefined);
        setIsVisibleDeleteModal(false);
        showSnackbar('Quadra Esportiva deletada com sucesso!', 'success');
        refetchAll();
      });
    }
  };

  const handleCancelDeletion = () => {
    setIsVisibleDeleteModal(false);
    setSelectedToDelete(undefined);
  };

  const handleSaveForm = async () => {
    setSelectedToEdit(undefined);
    await refetchAll();
    setIsShowLoadingComponent(false);
  };

  const handleCloseEditionModal = () => {
    setSelectedToEdit(undefined);
    setIsVisibleEditModal(false);
  };

  useEffect(() => {
    if (halls && totalHalls) {
      setIsShowLoadingComponent(false);
    }
  }, [halls, totalHalls]);

  return (
    <>
      <TableComponent
        columns={columns}
        rows={halls ?? []}
        sortByName={'name'}
        isLoading={isLoadingHalls || isLoadingCount}
        title="Quadras Esportivas"
        totalCount={totalHalls}
        onSubmitMainFilter={refetchAll}
        onChangeMainFilter={(search) =>
          setFilter((prev) => ({ ...prev, search }))
        }
        onChangePageOrRowsPerPage={setPaginationParams}
        additionalTitleComponent={
          <ButtonComponent
            onClick={() => setIsVisibleEditModal(true)}
            startIcon={<Add />}
          >
            {t('sportsPage.blocks.newBlock')}
          </ButtonComponent>
        }
      />
      <ConfirmDeleteModalComponent
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
        isVisibleDeleteDialog={isVisibleDeleteModal}
        message={`Deseja deletar a quadra ${selectedToDelete?.name} ?`}
        setIsVisibleDeleteDialog={setIsVisibleDeleteModal}
        title={'Deletar Quadra'}
      />
      <SportHallEditModal
        isOpen={isVisibleEditModal}
        setIsOpen={setIsVisibleEditModal}
        onClose={handleCloseEditionModal}
        selectedHall={selectedToEdit}
        handleSaveForm={handleSaveForm}
      />
    </>
  );
};

export default SportsHallsPage;
